import { useCallback, useEffect, useState } from 'react'

/**
 * LocalStorage-backed hook for the post-payment guided tour.
 *
 * Two flags persist across reloads + tabs:
 *   - `seen`: once-per-device "user already finished or skipped this tour"
 *     bit. Mirror of useFirstLoginTour's `seen` so the two tours behave
 *     consistently from the user's perspective.
 *   - `pending`: a "fire on next /dashboard mount" handoff flag set by
 *     `PaymentSuccess` after a successful Stripe checkout. The dashboard
 *     reads + consumes it on mount so the tour kicks in automatically
 *     even if the redirect lands a fraction of a second before mount
 *     finishes. Also useful for deferred upgrades (Stripe customer
 *     portal → tier transition watched by AuthContext realtime).
 *
 * The `?tour=post_payment` URL param is a third trigger path (used by
 * the Replay tile in /tutorials). The PostPaymentTour component
 * combines all three: `seen === false && (pending || urlForce)`.
 */
const STORAGE_KEY = 'bc_post_payment_tour_seen'
const PENDING_KEY = 'bc_post_payment_tour_pending'

function loadSeen(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function persistSeen(seen: boolean) {
  if (typeof window === 'undefined') return
  if (seen) window.localStorage.setItem(STORAGE_KEY, '1')
  else window.localStorage.removeItem(STORAGE_KEY)
}

function loadPending(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(PENDING_KEY) === '1'
  } catch {
    return false
  }
}

function persistPending(pending: boolean) {
  if (typeof window === 'undefined') return
  if (pending) window.localStorage.setItem(PENDING_KEY, '1')
  else window.localStorage.removeItem(PENDING_KEY)
}

export function usePostPaymentTour() {
  const [seen, setSeen] = useState<boolean>(loadSeen)
  const [pending, setPending] = useState<boolean>(loadPending)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setSeen(loadSeen())
      if (e.key === PENDING_KEY) setPending(loadPending())
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const markSeen = useCallback(() => {
    setSeen(true)
    persistSeen(true)
  }, [])

  const reset = useCallback(() => {
    setSeen(false)
    persistSeen(false)
  }, [])

  const markPending = useCallback(() => {
    setPending(true)
    persistPending(true)
  }, [])

  const consumePending = useCallback(() => {
    setPending(false)
    persistPending(false)
  }, [])

  return { seen, pending, markSeen, reset, markPending, consumePending }
}

/** Imperative setter used outside React render (e.g. PaymentSuccess.tsx
 *  after a successful checkout, before the dashboard mounts). Bypasses
 *  the hook so the value is persisted synchronously even if no consumer
 *  is mounted yet. */
export function markPostPaymentTourPending(): void {
  persistPending(true)
}
